import { Link, useLocation } from "react-router-dom";
import { Home, Search, Heart, User, MessageSquare, LogIn, Building2 } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useUnreadConversations } from "@/hooks/useUnreadConversations";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useUserTypes } from "@/hooks/useUserTypes";
import { isTextField, keyboardOpen as isKeyboardOpen } from "@/hooks/useChatViewport";

const MobileBottomNav = () => {
  const location = useLocation();
  const { user } = useAuth();
  const { isOwner } = useUserTypes();

  const messagesBadge = useUnreadConversations();

  // Hide the bottom nav when the on-screen keyboard is open. On iOS Safari
  // (and some Android browsers) `position: fixed` elements ride above the
  // keyboard, making the bar "float up" mid-screen. We detect the keyboard
  // by comparing visualViewport height to the layout viewport height, and
  // also hide while any text input / textarea / contenteditable is focused
  // as a fallback for browsers without visualViewport support.
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  useEffect(() => {
    const vv = (typeof window !== "undefined" && window.visualViewport) || null;
    const onResize = () => {
      if (!vv) return;
      // If the visual viewport is meaningfully shorter than the layout
      // viewport, assume the keyboard (or another OS chrome) is open.
      // A focused text field also counts: some browsers report a visible
      // area that matches the window while the keyboard is up (Oct 18, the
      // bar floated over the keyboard on Felipe's iPhone).
      setKeyboardOpen(isKeyboardOpen(window.innerHeight, vv.height) || isTextField(document.activeElement));
    };
    const onFocusIn = (e: FocusEvent) => {
      const t = e.target as HTMLElement | null;
      if (!t) return;
      const tag = t.tagName;
      if (
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        tag === "SELECT" ||
        t.isContentEditable
      ) {
        // Inputs of type button/checkbox/radio shouldn't trigger keyboard.
        if (tag === "INPUT") {
          const type = (t as HTMLInputElement).type;
          if (["button", "submit", "reset", "checkbox", "radio", "range", "color", "file"].includes(type)) return;
        }
        setKeyboardOpen(true);
      }
    };
    const onFocusOut = () => {
      // Defer so re-focusing another input doesn't flicker the bar.
      setTimeout(() => {
        const active = document.activeElement as HTMLElement | null;
        if (
          !active ||
          (active.tagName !== "INPUT" &&
            active.tagName !== "TEXTAREA" &&
            active.tagName !== "SELECT" &&
            !active.isContentEditable)
        ) {
          setKeyboardOpen(false);
        }
      }, 50);
    };
    vv?.addEventListener("resize", onResize);
    document.addEventListener("focusin", onFocusIn);
    document.addEventListener("focusout", onFocusOut);
    return () => {
      vv?.removeEventListener("resize", onResize);
      document.removeEventListener("focusin", onFocusIn);
      document.removeEventListener("focusout", onFocusOut);
    };
  }, []);

  // Publish the space the page must reserve under its content for this bar:
  // the bar's measured height (0 on md+ where it is display:none), and 0 while
  // the keyboard is up since the bar slides away then. index.css holds the
  // pre-mount default; consumers read var(--app-bottom-nav-reserve).
  const navRef = useRef<HTMLElement>(null);
  useLayoutEffect(() => {
    const el = navRef.current;
    if (!el) return;
    const root = document.documentElement;
    const apply = () =>
      root.style.setProperty(
        "--app-bottom-nav-reserve",
        keyboardOpen ? "0px" : `${Math.round(el.getBoundingClientRect().height)}px`,
      );
    apply();
    const observer = new ResizeObserver(apply);
    observer.observe(el);
    return () => {
      observer.disconnect();
      root.style.removeProperty("--app-bottom-nav-reserve");
    };
  }, [keyboardOpen]);

  // Same consumer bottom nav for everyone, admins reach /admin via the
  // Shield link in the Navbar (desktop) or the mobile menu (mobile).
  const items = user
    ? [
        { to: "/", icon: Home, label: "Home" },
        { to: "/properties", icon: Search, label: "Browse" },
        // Owners get a direct route to their hub; renters keep Saved.
        isOwner
          ? { to: "/dashboard", icon: Building2, label: "My Homes" }
          : { to: "/favorites", icon: Heart, label: "Saved" },
        { to: "/messages", icon: MessageSquare, label: "Messages", badge: messagesBadge },
        { to: "/profile", icon: User, label: "Profile" },
      ]
    : [
        { to: "/", icon: Home, label: "Home" },
        { to: "/properties", icon: Search, label: "Browse" },
        { to: "/login", icon: LogIn, label: "Sign In", highlight: true },
      ];

  return (
    <nav
      ref={navRef}
      aria-hidden={keyboardOpen}
      className={`md:hidden fixed bottom-0 left-0 right-0 z-50 glass-surface border-t border-border/60 safe-area-bottom transition-transform duration-150 ${
        keyboardOpen ? "translate-y-full pointer-events-none" : "translate-y-0"
      }`}
    >
      <div className="flex items-center px-2 py-1.5 justify-around">
        {items.map((item) => {
          const active = location.pathname === item.to;
          return (
            <Link
              key={item.to}
              to={item.to}
              className={`flex flex-1 min-w-0 flex-col items-center gap-0.5 px-1 py-1 rounded-lg transition-colors ${
                (item as any).highlight
                  ? "text-primary"
                  : active
                    ? "text-primary"
                    : "text-muted-foreground"
              }`}
            >
              <div className="relative">
                <item.icon className={`h-5 w-5 ${active ? "stroke-[2.5]" : ""}`} />
                {!!(item as any).badge && (item as any).badge > 0 && (
                  <span className="absolute -top-1.5 -right-2 min-w-[16px] h-4 px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-semibold flex items-center justify-center leading-none">
                    {(item as any).badge > 9 ? "9+" : (item as any).badge}
                  </span>
                )}
              </div>
              <span className="text-[10px] font-medium leading-none max-w-full truncate">{item.label}</span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
};

export default MobileBottomNav;
